import React, { useState, useEffect, useContext, useMemo, useRef } from "react";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsApp } from "@material-ui/icons";
import { Grid, ListItemText, MenuItem, Select } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { toast } from "react-toastify";
//import ShowTicketOpen from "../ShowTicketOpenModal";

const normalizeId = (v) => {
  if (v === null || v === undefined) return "";
  return String(v);
};

const useStyles = makeStyles((theme) => ({
  online: {
    fontSize: 11,
    color: "#25d366",
  },
  offline: {
    fontSize: 11,
    color: "#e1306c",
  },
}));

const digitsOnly = (v) => String(v || "").replace(/\D/g, "");
const isProbablyPhone = (v) => {
  const d = digitsOnly(v);
  return d.length >= 10; // 10/11 no BR
};

const filter = createFilterOptions({
  trim: true,
});

const isRealContactOption = (opt) => {
  if (!opt) return false;
  if (typeof opt === "string") return false;
  return !opt.kind && opt.id !== undefined && opt.id !== null;
};

// Normaliza BR: aceita comparar com/sem DDI 55
const normalizePhoneBR = (digits) => {
  const d = digitsOnly(digits);
  // BR comum: 10/11 (sem DDI) ou 12/13 (com 55)
  if ((d.length === 12 || d.length === 13) && d.startsWith("55"))
    return d.slice(2);
  return d;
};

const phonesEqual = (a, b) => {
  const na = normalizePhoneBR(a);
  const nb = normalizePhoneBR(b);
  return na !== "" && nb !== "" && na === nb;
};

const normalizeName = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

// contextCompanyId/contextWhatsappId:
// usados quando o modal é aberto a partir de uma conversa (ex: vCard),
// pra manter a empresa e a conexão (whatsapp) iguais às da conversa atual.
const NewTicketModal = ({
  modalOpen,
  onClose,
  initialContact = null,
  contextCompanyId,
  contextWhatsappId,
  isInternal = false,
}) => {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [newTicketNumber, setNewTicketNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const justPrefilledRef = useRef(false);

  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [newContact, setNewContact] = useState({});
  const [whatsapps, setWhatsapps] = useState([]);
  const [queues, setQueues] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [internalUsers, setInternalUsers] = useState([]);
  const [selectedInternalUser, setSelectedInternalUser] = useState("");
  const { user } = useContext(AuthContext);
  const { companyId: userCompanyId, whatsappId: userWhatsappId } = user;

  const isContextWhatsappLocked = useMemo(
    () => contextWhatsappId !== undefined && contextWhatsappId !== null,
    [contextWhatsappId],
  );

  const effectiveCompanyId = useMemo(
    () =>
      contextCompanyId !== undefined && contextCompanyId !== null
        ? contextCompanyId
        : userCompanyId,
    [contextCompanyId, userCompanyId],
  );
  const effectiveWhatsappId = useMemo(
    () =>
      contextWhatsappId !== undefined && contextWhatsappId !== null
        ? contextWhatsappId
        : userWhatsappId,
    [contextWhatsappId, userWhatsappId],
  );

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  useEffect(() => {
    if (!modalOpen) return;

    // Caso 1: veio um contato existente (tem id)
    if (initialContact?.id !== undefined && initialContact?.id !== null) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
      setSearchParam(renderContactLabel(initialContact));
      setNewTicketNumber(initialContact.number || "");
      justPrefilledRef.current = true;
      return;
    }

    // Caso 2: veio do vCard (sem id) -> pré-preenche número pra criar ticket direto
    const raw =
      initialContact?.number ||
      initialContact?.phone ||
      initialContact?.phoneRaw ||
      "";
    const num = digitsOnly(raw);
    if (num) {
      setSelectedContact(null);
      setSearchParam(num);
      setNewTicketNumber(num);
      justPrefilledRef.current = true;
    }
  }, [initialContact, modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        api
          .get(`/whatsapp`, {
            params: { companyId: effectiveCompanyId, session: 0 },
          })
          .then(({ data }) => setWhatsapps(data));
      };

      if (!isInternal) {
        if (effectiveWhatsappId !== null && effectiveWhatsappId !== undefined) {
          setSelectedWhatsapp(effectiveWhatsappId);
        }
        fetchContacts();
      } else {
        setSelectedWhatsapp("");
      }

      const fetchQueues = async () => {
        const normalizeQueueName = (name) => String(name || "").toLowerCase();
        const findInternalQueue = (queueList) =>
          (queueList || []).find(
            (queue) => normalizeQueueName(queue.name) === "interno",
          );
        const ensureInternalQueue = (queueList) => {
          const internalQueue = findInternalQueue(queueList);
          if (internalQueue) {
            setSelectedQueue(internalQueue.id);
          } else if (isInternal) {
            toastError(
              "Fila 'Interno' não encontrada. Configure uma fila chamada 'Interno'.",
            );
          }
        };
        if (user.profile !== "admin") {
          setQueues(user.queues);

          if (isInternal) {
            ensureInternalQueue(user.queues);
          } else if (user.queues.length === 1) {
            setSelectedQueue(user.queues[0].id);
          }

          return;
        }

        try {
          const { data: queues } = await api.get("/queue");
          setQueues(queues);

          if (isInternal) {
            ensureInternalQueue(queues);
          } else if (queues.length === 1) {
            setSelectedQueue(queues[0].id);
          }
        } catch (err) {
          toastError(i18n.t("newTicketModal.searchQueueError"));
        }
      };

      fetchQueues();
      setLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    modalOpen,
    effectiveCompanyId,
    effectiveWhatsappId,
    isInternal,
    user.profile,
    user.queues,
  ]);

  useEffect(() => {
    if (!modalOpen || !isInternal) return;
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        const userList = (data || []).map((u) => ({ id: u.id, name: u.name }));
        setInternalUsers(userList);
      } catch (err) {
        toastError(err);
      }
    };

    fetchUsers();
  }, [modalOpen, isInternal]);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  // const IconChannel = (channel) => {
  //   switch (channel) {
  //     case "facebook":
  //       return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
  //     case "instagram":
  //       return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
  //     case "whatsapp":
  //       return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
  //     default:
  //       return "error";
  //   }
  // };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setNewTicketNumber("");
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
    setSelectedContact(null);
    setSelectedInternalUser("");
    // volta pro whatsapp "da conversa" (ou do user) ao fechar
    if (effectiveWhatsappId !== null && effectiveWhatsappId !== undefined)
      setSelectedWhatsapp(effectiveWhatsappId);
    else setSelectedWhatsapp("");
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const handleSaveTicket = async (contactId) => {
    const normalizedNumber = newTicketNumber.replace(/\D/g, "");

    if (!isInternal && !contactId && normalizedNumber === "") {
      toast.error(i18n.t("newTicketModal.requiredContact"));
      return;
    }
    if (isInternal && selectedInternalUser === "") {
      toast.error(i18n.t("newTicketModal.requiredInternalUser"));
      return;
    }
    if (!isInternal && selectedQueue === "") {
      toast.error(i18n.t("newTicketModal.selectQueue"));
      return;
    }

    setLoading(true);
    try {
      const queueId = selectedQueue !== "" ? selectedQueue : null;
      const whatsappId = !isInternal
        ? selectedWhatsapp !== ""
          ? selectedWhatsapp
          : null
        : effectiveWhatsappId !== null && effectiveWhatsappId !== undefined
          ? effectiveWhatsappId
          : selectedWhatsapp !== ""
            ? selectedWhatsapp
            : null;
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        number: !isInternal && !contactId ? normalizedNumber : undefined,
        queueId,
        whatsappId,
        isInternal,
        internalUserId: isInternal ? selectedInternalUser : undefined,
        userId: user.id,
        status: "open",
      });

      onClose(ticket);
    } catch (err) {
      console.log(err);
      const ticket = err?.response?.data?.error;
      console.log(ticket);

      if (ticket === "ERR_OTHER_OPEN_TICKET") toastError(err);

      if (ticket && typeof ticket === "object") {
        if (ticket.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(ticket.user.name);
          setQueueTicketOpen(ticket.queue.name);
        } else {
          setOpenAlert(false);
          setUserTicketOpen("");
          setQueueTicketOpen("");
          setLoading(false);
          onClose(ticket);
        }
      } else {
        setOpenAlert(false);
        setUserTicketOpen("");
        setQueueTicketOpen("");
        setLoading(false);
        if (ticket) toastError(err);
      }
    }
    setLoading(false);
  };

  const handleSelectOption = (e, newValue) => {
    // freeSolo pode mandar string
    if (typeof newValue === "string") {
      setSelectedContact(null);
      setSearchParam(newValue);
      setNewTicketNumber(digitsOnly(newValue));
      return;
    }

    // contato existente
    if (newValue?.number && !newValue.kind) {
      setSelectedContact(newValue);
      setSearchParam(renderContactLabel(newValue));
      setNewTicketNumber(newValue.number);
      return;
    }

    // ação: adicionar contato
    if (newValue?.kind === "add") {
      setNewContact({ name: newValue.inputValue });
      setContactModalOpen(true);
      return;
    }

    // ação: enviar para (criar ticket direto)
    if (newValue?.kind === "send") {
      setSelectedContact(null);
      setSearchParam(newValue.inputValue);
      setNewTicketNumber(newValue.number || digitsOnly(newValue.inputValue));
      // opcional: já cria o ticket ao clicar:
      // handleSaveTicket(undefined);
      return;
    }

    setSelectedContact(null);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = (contact) => {
    handleSaveTicket(contact.id);
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);

    const input = String(params.inputValue || "").trim();
    const digits = digitsOnly(input);
    const inputLooksPhone = isProbablyPhone(input);

    if (input === "" || loading || input.length < 3) return filtered;

    const hasAnyRealMatch = filtered.some(isRealContactOption);

    // Se tem match exato, não mostra ações.
    // Se só tem "semelhante", mostra ações (junto com os resultados).
    let hasExactMatch = false;

    if (hasAnyRealMatch) {
      if (inputLooksPhone) {
        // match exato de telefone (BR com/sem 55)
        hasExactMatch = filtered.some(
          (opt) => isRealContactOption(opt) && phonesEqual(opt.number, digits),
        );
      } else {
        // match exato de nome
        const n = normalizeName(input);
        hasExactMatch = filtered.some(
          (opt) => isRealContactOption(opt) && normalizeName(opt.name) === n,
        );
      }
    }

    if (!hasExactMatch) {
      // 1) opção "Adicionar ..." (sempre)
      filtered.push({
        kind: "add",
        name: input,
        inputValue: input,
      });

      // 2) opção "Enviar para ..." (só se parecer telefone)
      if (inputLooksPhone) {
        filtered.push({
          kind: "send",
          name: input,
          inputValue: input,
          number: digits, // já normalizado
        });
      }
    }
    return filtered;
  };

  const renderOption = (option) => {
    // contato real
    if (option.number && !option.kind) {
      return (
        <Typography
          component="span"
          style={{
            fontSize: 14,
            marginLeft: "10px",
            display: "inline-flex",
            alignItems: "center",
            lineHeight: "2",
          }}
        >
          {renderContactLabel(option)}
        </Typography>
      );
    }

    // ações artificiais
    if (option.kind === "add") {
      return `${i18n.t("newTicketModal.add")} ${option.inputValue}`;
    }

    if (option.kind === "send") {
      return `Enviar para ${option.inputValue}`;
    }

    return option.name || "";
  };

  const renderOptionLabel = (option) => {
    if (typeof option === "string") return option;
    if (option.number && !option.kind) return renderContactLabel(option); // contato real
    return option.inputValue || option.name || "";
  };

  const renderContactLabel = (contact) => {
    const contactNickname = contact?.nickname ? ` (${contact.nickname})` : "";
    return `${contact.name}${contactNickname} - ${contact.number}`;
  };

  const renderContactAutocomplete = () => {
    if (isInternal) {
      return null;
    }
    if (!initialContact || initialContact.id === undefined) {
      return (
        <Grid xs={12} item>
          <Autocomplete
            fullWidth
            options={options}
            loading={loading}
            clearOnBlur={false}
            autoHighlight
            freeSolo
            clearOnEscape
            value={selectedContact}
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            inputValue={searchParam}
            onInputChange={(event, newInputValue, reason) => {
              // MUI pode disparar "reset" para "" ao perder foco (ex: clicar no Select)
              // REGRA: não apagar valor já digitado/preenchido
              if (
                reason === "reset" &&
                newInputValue === "" &&
                searchParam !== ""
              )
                return;

              // MUI às vezes dispara "reset" limpando logo após abrir
              if (
                reason === "reset" &&
                justPrefilledRef.current &&
                newInputValue === ""
              )
                return;

              justPrefilledRef.current = false;
              setSearchParam(newInputValue);
              setNewTicketNumber(newInputValue.replace(/\D/g, ""));
              if (newInputValue === "") {
                setSelectedContact(null);
              }
            }}
            onChange={(e, newValue) => handleSelectOption(e, newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n.t("newTicketModal.fieldLabel")}
                variant="outlined"
                autoFocus
                onKeyPress={(e) => {
                  if (loading || isSubmitDisabled) return;
                  else if (e.key === "Enter") {
                    handleSaveTicket(selectedContact?.id);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      );
    }
    return null;
  };

  const isSubmitDisabled = isInternal
    ? selectedInternalUser === ""
    : selectedQueue === "" || (!selectedContact && newTicketNumber === "");

  return (
    <>
      <ContactModal
        open={contactModalOpen}
        initialValues={newContact}
        onClose={handleCloseContactModal}
        onSave={handleAddNewContactTicket}
      ></ContactModal>
      <Dialog open={modalOpen} onClose={handleClose}>
        <DialogTitle id="form-dialog-title">
          {i18n.t("newTicketModal.title")}
        </DialogTitle>
        <DialogContent dividers>
          <Grid style={{ width: 300 }} container spacing={2}>
            {/* CONTATO */}
            {renderContactAutocomplete()}
            {/* FILA */}
            {!isInternal && (
              <Grid xs={12} item>
                <Select
                  required
                  fullWidth
                  displayEmpty
                  variant="outlined"
                  value={selectedQueue}
                  onChange={(e) => {
                    setSelectedQueue(e.target.value);
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                  renderValue={() => {
                    if (selectedQueue === "") {
                      return i18n.t("newTicketModal.selectQueue");
                    }

                    const queue = queues.find((q) => q.id === selectedQueue);
                    return queue.name;
                  }}
                >
                  {queues?.length > 0 &&
                    queues.map((queue, key) => (
                      <MenuItem dense key={key} value={queue.id}>
                        <ListItemText primary={queue.name} />
                      </MenuItem>
                    ))}
                </Select>
              </Grid>
            )}
            {isInternal && (
              <Grid xs={12} item>
                <Select
                  required
                  fullWidth
                  displayEmpty
                  variant="outlined"
                  value={selectedInternalUser}
                  onChange={(e) => {
                    setSelectedInternalUser(e.target.value);
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                  renderValue={() => {
                    if (selectedInternalUser === "") {
                      return i18n.t("newTicketModal.selectInternalUser");
                    }

                    const internalUser = internalUsers.find(
                      (u) => u.id === selectedInternalUser,
                    );
                    return internalUser?.name || "";
                  }}
                >
                  {internalUsers?.length > 0 &&
                    internalUsers.map((internalUser) => (
                      <MenuItem
                        dense
                        key={internalUser.id}
                        value={internalUser.id}
                      >
                        <ListItemText primary={internalUser.name} />
                      </MenuItem>
                    ))}
                </Select>
              </Grid>
            )}
            {/* CONEXAO */}
            {!isInternal && (
              <Grid xs={12} item>
                <Select
                  required
                  fullWidth
                  displayEmpty
                  variant="outlined"
                  value={selectedWhatsapp}
                  onChange={(e) => {
                    setSelectedWhatsapp(e.target.value);
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                  renderValue={() => {
                    if (selectedWhatsapp === "") {
                      return i18n.t("newTicketModal.selectConection");
                    }
                    const whatsapp = (whatsapps || []).find(
                      (w) =>
                        normalizeId(w.id) === normalizeId(selectedWhatsapp),
                    );
                    // ainda não carregou / id não existe na lista
                    if (!whatsapp)
                      return i18n.t("newTicketModal.selectConection");
                    return whatsapp.name;
                  }}
                >
                  {whatsapps?.length > 0 &&
                    whatsapps.map((whatsapp, key) => (
                      <MenuItem dense key={key} value={whatsapp.id}>
                        <ListItemText
                          primary={
                            <>
                              {/* {IconChannel(whatsapp.channel)} */}
                              <Typography
                                component="span"
                                style={{
                                  fontSize: 14,
                                  marginLeft: "10px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  lineHeight: "2",
                                }}
                              >
                                {whatsapp.name} &nbsp;{" "}
                                <p
                                  className={
                                    whatsapp.status === "CONNECTED"
                                      ? classes.online
                                      : classes.offline
                                  }
                                >
                                  ({whatsapp.status})
                                </p>
                              </Typography>
                            </>
                          }
                        />
                      </MenuItem>
                    ))}
                </Select>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="secondary"
            disabled={loading}
            variant="outlined"
          >
            {i18n.t("newTicketModal.buttons.cancel")}
          </Button>
          <ButtonWithSpinner
            variant="contained"
            type="button"
            disabled={isSubmitDisabled}
            onClick={() => handleSaveTicket(selectedContact?.id)}
            color="primary"
            loading={loading}
          >
            {i18n.t("newTicketModal.buttons.ok")}
          </ButtonWithSpinner>
        </DialogActions>
        {/* <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
			  /> */}
      </Dialog>
    </>
  );
};
export default NewTicketModal;
